// ─────────────────────────────────────────────────────────────────────────────
// THREE.JS WEBGL RENDERER
// Scene layers (back → front):
//   0. Video background plane (VideoTexture)
//   1. Garment plane (ShaderMaterial — landmark-driven warp)
//   2. Segmentation occlusion plane (person foreground mask)
// ─────────────────────────────────────────────────────────────────────────────
import * as THREE from "three";
import type { TorsoGeometry } from "./pose";
import type { DepthMap } from "./depth-model";
import type { SAM2Mask } from "./sam2-client";

// ── Garment vertex shader — trapezoid warp driven by torso landmarks ──────────
const GARMENT_VERT = /* glsl */ `
  precision highp float;

  uniform vec2 uAnchor;     // normalized screen pos of shoulder midpoint
  uniform float uWidth;     // shoulder width in clip space
  uniform float uHeight;    // torso height in clip space
  uniform float uSkew;      // hip-shoulder skew (perspective tilt)

  varying vec2 vUv;

  void main() {
    vUv = uv;

    // Build clip-space position from unit plane [-0.5, 0.5]
    vec2 p = position.xy;

    // Apply shoulder width scale + torso height scale
    p.x *= uWidth * 1.15;   // 15% wider than shoulder-to-shoulder for fabric
    p.y *= uHeight * 1.25;  // 25% taller to include collar + hem

    // Trapezoid perspective: top edge slightly wider than bottom
    float taper = 1.0 + (p.y + 0.5) * uSkew * 0.08;
    p.x *= taper;

    // Translate to shoulder anchor in clip space
    p += uAnchor;

    gl_Position = vec4(p, 0.0, 1.0);
  }
`;

// ── Garment fragment shader — texture + blend mode ────────────────────────────
const GARMENT_FRAG = /* glsl */ `
  precision highp float;

  uniform sampler2D uGarmentTex;
  uniform float uOpacity;
  uniform float uBrightness;
  uniform float uWhiteThresh;  // white-key threshold (0.85 default)
  uniform float uContrast;

  varying vec2 vUv;

  void main() {
    vec4 col = texture2D(uGarmentTex, vUv);

    // Discard if texture not loaded (null → vec4(0)) — prevents black rectangle
    if (col.a < 0.01 && col.r < 0.01 && col.g < 0.01 && col.b < 0.01) discard;

    // If texture has own alpha (PNG), use it
    float texAlpha = col.a;

    // White-key: smooth fade from opaque at (thresh-0.08) to transparent at thresh
    float lum = dot(col.rgb, vec3(0.299, 0.587, 0.114));
    float keyAlpha = 1.0 - smoothstep(uWhiteThresh - 0.08, uWhiteThresh, lum);

    // Use min of key alpha and texture alpha — keeps hard-edge PNGs crisp
    float finalAlpha = min(keyAlpha, texAlpha > 0.01 ? texAlpha : 1.0);

    if (finalAlpha < 0.04) discard;

    // Brightness + contrast
    col.rgb = ((col.rgb - 0.5) * uContrast + 0.5) * uBrightness;
    col.a = finalAlpha * uOpacity;

    gl_FragColor = col;
  }
`;

export class ARRenderer {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;

  // Scene objects
  private bgPlane!: THREE.Mesh;
  private garmentMesh!: THREE.Mesh;
  private occlusionMesh!: THREE.Mesh;
  private garment3D: THREE.Object3D | null = null;

  // Cloth simulation mesh (replaces garmentMesh when cloth is active)
  private clothMesh: THREE.Mesh | null = null;

  // Arm occlusion mask (from body-segmentation landmark polygons)
  // Merged into the occlusion texture so arms occlude the garment
  private armMaskData: Uint8ClampedArray | null = null;
  private armMaskW = 0;
  private armMaskH = 0;

  // Latest depth map from Depth Anything V2 (for z-ordering info, stored for future use)
  private latestDepthMap: DepthMap | null = null;

  // Textures
  private videoTex!: THREE.VideoTexture;
  private garmentTex: THREE.Texture | null = null;
  private occlusionTex!: THREE.DataTexture;

  // Garment uniforms
  private garmentUniforms!: {
    uAnchor:      { value: THREE.Vector2 };
    uWidth:       { value: number };
    uHeight:      { value: number };
    uSkew:        { value: number };
    uGarmentTex:  { value: THREE.Texture | null };
    uOpacity:     { value: number };
    uBrightness:  { value: number };
    uWhiteThresh: { value: number };
    uContrast:    { value: number };
  };

  constructor(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
    // ── Renderer ──────────────────────────────────────────────────────────────
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: false,
      antialias: false,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.sortObjects = true;   // spec requirement for correct depth order
    this.renderer.setClearColor(0x000000, 1);

    // ── Camera: orthographic –1→1 on both axes ────────────────────────────────
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.camera.position.z = 1;

    this.scene = new THREE.Scene();

    // ── Background video plane ────────────────────────────────────────────────
    this.videoTex = new THREE.VideoTexture(video);
    this.videoTex.minFilter = THREE.LinearFilter;
    this.videoTex.magFilter = THREE.LinearFilter;
    this.videoTex.format = THREE.RGBFormat;

    const bgGeo = new THREE.PlaneGeometry(2, 2);
    const bgMat = new THREE.MeshBasicMaterial({
      map: this.videoTex,
      depthTest: false,
      depthWrite: false,
    });
    this.bgPlane = new THREE.Mesh(bgGeo, bgMat);
    this.bgPlane.renderOrder = 0;
    this.scene.add(this.bgPlane);

    // ── Garment plane (ShaderMaterial) ────────────────────────────────────────
    this.garmentUniforms = {
      uAnchor:      { value: new THREE.Vector2(0, 0) },
      uWidth:       { value: 0.6 },
      uHeight:      { value: 0.6 },
      uSkew:        { value: 0 },
      uGarmentTex:  { value: null },
      uOpacity:     { value: 0.85 },
      uBrightness:  { value: 1.0 },
      uWhiteThresh: { value: 0.88 },  // removes white/cream backgrounds
      uContrast:    { value: 1.0 },
    };

    const garmentMat = new THREE.ShaderMaterial({
      vertexShader: GARMENT_VERT,
      fragmentShader: GARMENT_FRAG,
      uniforms: this.garmentUniforms,
      transparent: true,
      depthTest: false,
      side: THREE.FrontSide,
    });

    const garmentGeo = new THREE.PlaneGeometry(1, 1, 4, 8); // subdivided for warp
    this.garmentMesh = new THREE.Mesh(garmentGeo, garmentMat);
    this.garmentMesh.renderOrder = 1;
    this.scene.add(this.garmentMesh);

    // ── Occlusion plane (person foreground mask) ──────────────────────────────
    // Renders VIDEO back on top of the garment where the person is present.
    // map      = VideoTexture  → color = live camera pixels
    // alphaMap = DataTexture   → alpha = person confidence (R channel)
    // Where confidence is high (person), video is opaque → hides garment behind them.
    // Where confidence is low (background), transparent → garment shows through.
    this.occlusionTex = new THREE.DataTexture(
      new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat
    );
    const occlusionMat = new THREE.MeshBasicMaterial({
      map:      this.videoTex,      // color source = live video
      alphaMap: this.occlusionTex,  // alpha source  = person mask (R channel)
      transparent: true,
      depthTest: false,
    });
    const occGeo = new THREE.PlaneGeometry(2, 2);
    this.occlusionMesh = new THREE.Mesh(occGeo, occlusionMat);
    this.occlusionMesh.renderOrder = 2;
    this.scene.add(this.occlusionMesh);
  }

  // ── Canvas access ────────────────────────────────────────────────────────
  getCanvas(): HTMLCanvasElement { return this.renderer.domElement; }

  // ── Set canvas size ───────────────────────────────────────────────────────
  // Always size to SCREEN dimensions (CSS px × DPR) so the buffer matches
  // what the browser displays. DPR is already set in the constructor.
  resize(w: number, h: number) {
    this.renderer.setSize(w, h, false);
  }

  // ── Video aspect-ratio correction (cover fit) ─────────────────────────────
  // Call once when video metadata is loaded (or on resize).
  // Adjusts the VideoTexture UV repeat/offset so the video fills the canvas
  // without distortion — same as CSS "object-fit: cover".
  updateVideoAspect(videoW: number, videoH: number, canvasW: number, canvasH: number) {
    if (!videoW || !videoH || !canvasW || !canvasH) return;
    const videoAR  = videoW / videoH;
    const canvasAR = canvasW / canvasH;

    if (videoAR > canvasAR) {
      // Video wider than canvas — crop left/right, fit height
      const scaleX = canvasAR / videoAR;
      this.videoTex.repeat.set(scaleX, 1);
      this.videoTex.offset.set((1 - scaleX) / 2, 0);
    } else {
      // Video taller than canvas — crop top/bottom, fit width
      const scaleY = videoAR / canvasAR;
      this.videoTex.repeat.set(1, scaleY);
      this.videoTex.offset.set(0, (1 - scaleY) / 2);
    }
    this.videoTex.needsUpdate = true;
  }

  // ── Load garment texture from URL ─────────────────────────────────────────
  setGarmentImage(url: string) {
    this.setUseGLBGarment(false);
    // Hide mesh while texture loads — prevents black rectangle during fetch
    this.garmentMesh.visible = false;
    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    loader.load(url, (tex) => {
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = true;
      this.garmentTex?.dispose();
      this.garmentTex = tex;
      this.garmentUniforms.uGarmentTex.value = tex;
      // Only show mesh once texture is ready
      this.garmentMesh.visible = true;
    }, undefined, (err) => {
      console.warn('[AR] Garment texture load failed:', url, err);
      // Leave mesh hidden — no black rectangle on failure
    });
  }

  clearGarment() {
    this.garmentUniforms.uGarmentTex.value = null;
    this.garmentMesh.visible = false;
    this.removeClothMesh();
  }

  // ── Cloth mesh — activated when cloth physics is enabled ──────────────────
  setClothMesh(geo: THREE.BufferGeometry, texUrl: string) {
    this.removeClothMesh();
    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    loader.load(texUrl, (tex) => {
      const mat = new THREE.MeshStandardMaterial({
        map: tex,
        side: THREE.DoubleSide,
        transparent: true,
        alphaTest: 0.04,
        roughness: 0.8,
        metalness: 0.0,
      });
      // White-key: discard near-white (same as garment shader)
      mat.onBeforeCompile = (shader) => {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <alphatest_fragment>',
          `float lum = dot(diffuseColor.rgb, vec3(0.299,0.587,0.114));
           float ka = 1.0 - smoothstep(0.80, 0.88, lum);
           diffuseColor.a *= ka;
           if (diffuseColor.a < 0.04) discard;
           #include <alphatest_fragment>`
        );
      };
      this.clothMesh = new THREE.Mesh(geo, mat);
      this.clothMesh.renderOrder = 1;
      this.clothMesh.castShadow = false;
      this.scene.add(this.clothMesh);
      // Hide the flat 2D garment plane — cloth takes over
      this.garmentMesh.visible = false;
    });
  }

  removeClothMesh() {
    if (!this.clothMesh) return;
    this.scene.remove(this.clothMesh);
    (this.clothMesh.material as THREE.Material).dispose();
    this.clothMesh = null;
    // Restore flat garment if texture is loaded
    if (this.garmentUniforms.uGarmentTex.value) {
      this.garmentMesh.visible = true;
    }
  }

  addGarmentScene(scene: THREE.Object3D) {
    if (this.garment3D) {
      this.scene.remove(this.garment3D);
    }

    this.garment3D = scene;
    this.garment3D.visible = true;
    this.scene.add(this.garment3D);
    this.setUseGLBGarment(true);
  }

  removeGarmentScene() {
    if (!this.garment3D) return;
    this.scene.remove(this.garment3D);
    this.garment3D = null;
    this.setUseGLBGarment(false);
  }

  setUseGLBGarment(useGLB: boolean) {
    this.garmentMesh.visible = !useGLB;
    if (this.garment3D) {
      this.garment3D.visible = useGLB;
    }
  }

  // ── Update overlay settings ───────────────────────────────────────────────
  setOpacity(v: number)    { this.garmentUniforms.uOpacity.value    = v; }
  setBrightness(v: number) { this.garmentUniforms.uBrightness.value = v / 100; }
  setContrast(v: number)   { this.garmentUniforms.uContrast.value = v / 100; }

  // ── Mirror video for front camera ─────────────────────────────────────────
  setMirror(mirror: boolean) {
    this.bgPlane.scale.x = mirror ? -1 : 1;
    this.occlusionMesh.scale.x = mirror ? -1 : 1;
    // Garment mesh does NOT mirror — garment stays correct orientation
  }

  // ── Update garment position from pose landmarks ───────────────────────────
  updateFromPose(torso: TorsoGeometry, isMirrored: boolean) {
    // Convert normalized (0–1, y-down) coords to clip space (−1→1, y-up)
    let anchorX = torso.shoulderMid.x * 2 - 1;
    const anchorY = -(torso.shoulderMid.y * 2 - 1);

    if (isMirrored) anchorX = -anchorX;

    // Shift anchor down — move from shoulder midpoint to chest center
    const torsoOffsetY = -(torso.torsoHeight * 2) * 0.35;

    this.garmentUniforms.uAnchor.value.set(anchorX, anchorY + torsoOffsetY);
    // Scale with padding: shoulders +35%, height +45% to cover collar and waist
    this.garmentUniforms.uWidth.value  = Math.max(0.3, torso.shoulderWidth * 2.7);
    this.garmentUniforms.uHeight.value = Math.max(0.4, torso.torsoHeight   * 2.9);

    // Skew based on shoulder tilt
    const shoulderTilt = 0; // Could compute from left/right shoulder Y diff
    this.garmentUniforms.uSkew.value = shoulderTilt;
  }

  // ── Fallback: heuristic position (no pose) ───────────────────────────────
  setHeuristicPosition(isBottom: boolean) {
    this.garmentUniforms.uAnchor.value.set(0, isBottom ? -0.15 : 0.05);
    this.garmentUniforms.uWidth.value = 0.72;
    this.garmentUniforms.uHeight.value = isBottom ? 0.7 : 0.65;
    this.garmentUniforms.uSkew.value = 0;
  }

  // ── Arm occlusion: merge arm polygon mask into the occlusion texture ─────
  // Called every frame from pipeline.ts after computeArmMask().
  // Where armMask[i]=1, we ensure the occlusion texture is opaque so the
  // video (person's arm) renders on top of the garment.
  updateArmOcclusionMask(armMask: Uint8ClampedArray, w: number, h: number) {
    this.armMaskData = armMask;
    this.armMaskW    = w;
    this.armMaskH    = h;
  }

  // ── Store latest depth map (Depth Anything V2) ───────────────────────────
  // Used by future depth-based z-ordering / garment layering passes.
  setLatestDepthMap(map: DepthMap) {
    this.latestDepthMap = map;
  }

  getLatestDepthMap(): DepthMap | null {
    return this.latestDepthMap;
  }

  // ── Apply SAM2 person mask as high-quality occlusion source ─────────────
  // SAM2 gives pixel-perfect person silhouette. We use it to replace the
  // selfie-segmenter occlusion when available — much sharper edges.
  updateSAM2PersonMask(sam2Mask: SAM2Mask) {
    const { person, width, height } = sam2Mask;
    // Build RGBA ImageData: where person mask = 1, alpha = 255 (show video)
    const data = new Uint8ClampedArray(width * height * 4);
    const flat = person.data;
    for (let i = 0; i < width * height; i++) {
      // R channel used as alpha by alphaMap — white = opaque = show video
      const isArm = this.armMaskData
        ? (() => {
            const ax = Math.min(this.armMaskW - 1, Math.round((i % width) * this.armMaskW / width));
            const ay = Math.min(this.armMaskH - 1, Math.round(Math.floor(i / width) * this.armMaskH / height));
            return this.armMaskData![ay * this.armMaskW + ax] > 0;
          })()
        : false;
      // Arms always occlude (show video); person mask shows video where body is
      const alpha = (flat[i * 4 + 3] > 128 || isArm) ? 255 : 0;
      data[i * 4]     = alpha;
      data[i * 4 + 1] = alpha;
      data[i * 4 + 2] = alpha;
      data[i * 4 + 3] = alpha;
    }
    this.updateOcclusionMask(new ImageData(data, width, height));
  }

  // ── Update occlusion mask each frame ─────────────────────────────────────
  updateOcclusionMask(maskData: ImageData) {
    const mat = this.occlusionMesh.material as THREE.MeshBasicMaterial;

    // Merge arm mask into occlusion: arm pixels are always opaque (show video)
    let finalData = maskData.data as Uint8ClampedArray;
    if (this.armMaskData && this.armMaskData.length > 0) {
      // Modify a copy so we don't mutate the incoming ImageData
      finalData = new Uint8ClampedArray(maskData.data);
      const scaleX = this.armMaskW / maskData.width;
      const scaleY = this.armMaskH / maskData.height;
      for (let i = 0; i < maskData.width * maskData.height; i++) {
        const ax = Math.min(this.armMaskW - 1, Math.round((i % maskData.width) * scaleX));
        const ay = Math.min(this.armMaskH - 1, Math.round(Math.floor(i / maskData.width) * scaleY));
        if (this.armMaskData[ay * this.armMaskW + ax]) {
          // Force full alpha on arm pixels — arm always occludes garment
          finalData[i * 4]     = 255;
          finalData[i * 4 + 1] = 255;
          finalData[i * 4 + 2] = 255;
          finalData[i * 4 + 3] = 255;
        }
      }
    }

    // Re-create DataTexture only when dimensions change
    if (
      this.occlusionTex.image.width  !== maskData.width ||
      this.occlusionTex.image.height !== maskData.height
    ) {
      this.occlusionTex.dispose();
      this.occlusionTex = new THREE.DataTexture(
        finalData,
        maskData.width,
        maskData.height,
        THREE.RGBAFormat,
        THREE.UnsignedByteType
      );
      mat.alphaMap = this.occlusionTex;
      mat.needsUpdate = true;
    } else {
      // Fast path: update existing texture data in place
      (this.occlusionTex.image as { data: Uint8ClampedArray }).data.set(finalData);
    }
    this.occlusionTex.needsUpdate = true;
  }

  // ── Draw one frame ────────────────────────────────────────────────────────
  render() {
    this.videoTex.needsUpdate = true;
    this.renderer.render(this.scene, this.camera);
  }

  // ── Snapshot current frame as data URL ───────────────────────────────────
  snapshot(quality = 0.88): string {
    this.render();
    return this.renderer.domElement.toDataURL("image/jpeg", quality);
  }

  dispose() {
    this.videoTex.dispose();
    this.garmentTex?.dispose();
    this.occlusionTex.dispose();
    this.renderer.dispose();
  }
}
